export const normalizeGttEnabled = (value, fallback = "") => {
  if (value === true) return "YES";
  if (value === false) return "NO";

  const normalizedValue = String(value ?? "")
    .trim()
    .toUpperCase();

  if (["YES", "Y", "TRUE", "1", "ENABLED"].includes(normalizedValue)) {
    return "YES";
  }

  if (["NO", "N", "FALSE", "0", "DISABLED"].includes(normalizedValue)) {
    return "NO";
  }

  return fallback;
};

export const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const calculateRemainingGainPercent = (targetPrice, currentPrice) => {
  const target = toFiniteNumber(targetPrice);
  const current = toFiniteNumber(currentPrice);

  if (!target || target <= 0 || current === null) {
    return null;
  }

  return Number((((current - target) / target) * 100).toFixed(2));
};

const roundTargetMetric = (value) => Number(value.toFixed(2));

/**
 * Measure how far price has travelled from the entry to the target.
 * The same formula works for long targets above the entry and short targets
 * below it. Display values are clamped to 0-100 while rawPercent preserves
 * overshoots and moves in the wrong direction for status/highlight decisions.
 */
export const calculateTargetProgress = (buyPrice, targetPrice, trackedPrice) => {
  const buy = toFiniteNumber(buyPrice);
  const target = toFiniteNumber(targetPrice);
  const tracked = toFiniteNumber(trackedPrice);

  if (
    buy === null ||
    target === null ||
    tracked === null ||
    buy <= 0 ||
    target <= 0 ||
    target === buy
  ) {
    return null;
  }

  const rawPercent = ((tracked - buy) / (target - buy)) * 100;
  if (!Number.isFinite(rawPercent)) return null;

  const roundedRawPercent = roundTargetMetric(rawPercent);
  const achievedPercent = Math.min(100, Math.max(0, rawPercent));
  const remainingPercent = Math.max(0, 100 - achievedPercent);

  return {
    rawPercent: roundedRawPercent,
    achievedPercent: roundTargetMetric(achievedPercent),
    remainingPercent: roundTargetMetric(remainingPercent),
    isTargetMet: rawPercent >= 100,
    needsAttention: roundedRawPercent >= 95 && rawPercent < 100,
  };
};

export const getLedgerDateKey = (value) => {
  if (!value) return "";
  const rawValue = String(value).trim();
  const isoDate = rawValue.match(/\d{4}-\d{2}-\d{2}/)?.[0];
  if (isoDate) return isoDate;

  const parsedDate = new Date(value);
  if (!Number.isNaN(parsedDate.getTime())) {
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return rawValue;
};

export const isIntradayTrade = (trade = {}) => {
  const buyDate = getLedgerDateKey(trade.buyDate ?? trade.buy_date);
  const sellDate = getLedgerDateKey(trade.sellDate ?? trade.sell_date);

  return Boolean(buyDate && sellDate && buyDate === sellDate);
};

const LEDGER_POSITION_NAMESPACE = "ledger-position-groups";

export const createLedgerPositionId = () =>
  `pos_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

/**
 * Read a partial-exit request after the edit form has been converted to the
 * database payload shape. Keeping this normalization here prevents the UI
 * from comparing camelCase fields against snake_case submission data.
 */
export const getPartialExitPlan = (
  originalRecord = {},
  submittedRecord = {},
  fallbackPositionId = null
) => {
  const originalQuantity = Number(
    originalRecord.quantity ?? originalRecord.total_quantity
  );
  const soldQuantity = Number(
    submittedRecord.sell_quantity ?? submittedRecord.sellQuantity
  );
  const sellPrice = submittedRecord.sell_price ?? submittedRecord.sellPrice;
  const sellDate = submittedRecord.sell_date ?? submittedRecord.sellDate;

  if (
    !Number.isFinite(originalQuantity) ||
    originalQuantity <= 0 ||
    !Number.isFinite(soldQuantity) ||
    soldQuantity <= 0 ||
    soldQuantity >= originalQuantity ||
    sellPrice === null ||
    sellPrice === undefined ||
    sellPrice === "" ||
    !sellDate
  ) {
    return null;
  }

  return {
    originalQuantity,
    soldQuantity,
    remainingQuantity: originalQuantity - soldQuantity,
    positionId:
      originalRecord.positionId ||
      originalRecord.position_id ||
      submittedRecord.position_id ||
      submittedRecord.positionId ||
      fallbackPositionId,
  };
};

export const getLedgerPositionStorageKey = (userId = "", portfolioId = "") =>
  `${LEDGER_POSITION_NAMESPACE}:${userId || "guest"}:${portfolioId || "default"}`;

export const readLedgerPositionAssignments = (storageKey) => {
  if (!storageKey || typeof window === "undefined") {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const writeLedgerPositionAssignments = (storageKey, assignments) => {
  if (!storageKey || typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify(assignments && typeof assignments === "object" ? assignments : {})
    );
  } catch (error) {
  }
};

export const mergeLedgerPositionAssignments = (storageKey, nextAssignments = {}) => {
  const currentAssignments = readLedgerPositionAssignments(storageKey);
  const mergedAssignments = {
    ...currentAssignments,
    ...nextAssignments,
  };
  writeLedgerPositionAssignments(storageKey, mergedAssignments);
  return mergedAssignments;
};

export const removeLedgerPositionAssignments = (storageKey, recordIds = []) => {
  if (!storageKey || !Array.isArray(recordIds) || !recordIds.length) {
    return;
  }

  const nextAssignments = { ...readLedgerPositionAssignments(storageKey) };
  recordIds.forEach((recordId) => {
    delete nextAssignments[String(recordId)];
  });
  writeLedgerPositionAssignments(storageKey, nextAssignments);
};

export const prepareUserLedgerData = (data, options = {}) => {
  const { positionAssignments = {} } = options;

  if (!Array.isArray(data)) {
    return [];
  }
  
  return data.map((item, index) => {
    // Ensure every item has an ID - use database ID if available, otherwise generate temporary one
    const itemId = item.id || `temp_${Date.now()}_${index}`;

    const preparedItem = {
      id: itemId,
      stockSymbol: item.stock_name,
      positionId:
        item.position_id ||
        item.positionId ||
        positionAssignments[String(itemId)] ||
        null,
      buyPrice: item.buy_price,
      targetPrice: item.target_price,
      buyDate: item.buy_date,
      quantity: item.quantity,
      sellPrice: item.sell_price,
      sellDate: item.sell_date,
      sellQuantity: item.sell_quantity,
      brokerage: item.brokerage,
      daysHold: item.days_hold,
      reasonToBuy: item.reason_to_buy,
      gttEnabled: normalizeGttEnabled(item.gtt_enabled, "NO"),
      profitLoss: item.profit_loss,
      amount: item.Amount,
      annualReturnGenerated: item.annual_return_generated,
      roce: item.roce,
      isPartialSell: item.is_partial_sell || false,
      tradeSource: item.trade_source || "manual",
      brokerVerifiedAt: item.broker_verified_at || null,
      brokerReference: item.broker_reference || null,
      tradeClassification: item.trade_classification || null,
      tradeSide: item.trade_side || null,
      entryExecutionCount: item.entry_execution_count || null,
      exitExecutionCount: item.exit_execution_count || null,
      entryFillCount: item.entry_fill_count || null,
      exitFillCount: item.exit_fill_count || null,
      importMetadata: item.import_metadata || null,
      chargeSource: item.charge_source || null,
    };

    if (isIntradayTrade(preparedItem)) {
      return {
        ...preparedItem,
        targetPrice: null,
        currentPrice: null,
        remainingGainPercent: null,
        targetProgressPercent: null,
        targetRemainingPercent: null,
      };
    }

    return preparedItem;
  });
};
